import { Box, Typography, Button, CircularProgress, Alert, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PresentationBuilder } from '../../../components/PresentationBuilder';
import { usePresentationWizard } from './usePresentationWizard';
import { ACCENT, ACCENT_LIGHT, btnPrimary, btnOutlined } from './PresentationWizard.styles';
import StepSidebar from './StepSidebar';
import DetailsStep from './DetailsStep';
import UploadStep from './UploadStep';
import ScheduleStep from './ScheduleStep';

function PresentationWizard({ editId }) {
  const navigate = useNavigate();
  const wizard = usePresentationWizard({ editId });

  return (
    <Box sx={{ display: 'flex', gap: '24px', minWidth: 0, overflow: 'hidden' }}>
      {/* Left Sidebar */}
      <StepSidebar
        steps={wizard.steps}
        activeStep={wizard.activeStep}
        currentStepIndex={wizard.currentStepIndex}
        stepOrder={wizard.stepOrder}
        validateStep={wizard.validateStep}
        validationErrors={wizard.validationErrors}
        onStepClick={wizard.setActiveStep}
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {wizard.editLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        )}

        {!wizard.editLoading && <>
          {/* Back Link */}
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: '18px' }} />}
            onClick={() => navigate('/settings')}
            sx={{
              textTransform: 'none',
              color: ACCENT,
              padding: '4px 8px',
              marginBottom: '8px',
              fontSize: '0.85rem',
              fontWeight: 500,
              borderRadius: '6px',
              '&:hover': { backgroundColor: ACCENT_LIGHT },
            }}
          >
            All Presentations
          </Button>

          {/* Title */}
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2A3547', fontSize: '28px', marginBottom: '32px' }}>
            {wizard.isEditMode ? 'Edit Presentation' : 'New Presentation'}
          </Typography>

          {/* General error alert */}
          {wizard.pptxError && wizard.activeStep !== 'creation-method' && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => wizard.setPptxError(null)}>
              {wizard.pptxError}
            </Alert>
          )}

          {/* Step content */}
          {wizard.activeStep === 'details' && (
            <DetailsStep
              name={wizard.name}
              setName={wizard.setName}
              description={wizard.description}
              setDescription={wizard.setDescription}
              validationErrors={wizard.validationErrors}
              setValidationErrors={wizard.setValidationErrors}
            />
          )}

          {wizard.activeStep === 'creation-method' && (
            <UploadStep
              pptxFile={wizard.pptxFile}
              pptxError={wizard.pptxError}
              setPptxError={wizard.setPptxError}
              dragActive={wizard.dragActive}
              fileInputRef={wizard.fileInputRef}
              handleBrowseFiles={wizard.handleBrowseFiles}
              handleFileChange={wizard.handleFileChange}
              handleDrag={wizard.handleDrag}
              handleDrop={wizard.handleDrop}
              clearPptx={wizard.clearPptx}
            />
          )}

          {wizard.activeStep === 'layout' && (
            <>

              {wizard.validationErrors.slides && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => wizard.setValidationErrors((prev) => { const { slides: _, ...rest } = prev; return rest; })}>
                  {wizard.validationErrors.slides}
                </Alert>
              )}
              <PresentationBuilder
                slides={wizard.slides}
                selectedIndex={wizard.selectedSlideIndex}
                onSelectSlide={wizard.setSelectedSlideIndex}
                onSlidesChange={wizard.setSlides}
                onTestPpt={wizard.handleTestPpt}
                testPptLoading={wizard.testPptLoading}
                decimalPlaces={wizard.decimalPlaces}
                onDecimalPlacesChange={wizard.setDecimalPlaces}
              />
            </>
          )}

          {wizard.activeStep === 'schedule' && (
            <ScheduleStep
              scheduleEnabled={wizard.scheduleEnabled}
              setScheduleEnabled={wizard.setScheduleEnabled}
              recurrence={wizard.recurrence}
              setRecurrence={wizard.setRecurrence}
              scheduleDay={wizard.scheduleDay}
              setScheduleDay={wizard.setScheduleDay}
              scheduleMonth={wizard.scheduleMonth}
              setScheduleMonth={wizard.setScheduleMonth}
              scheduleTime={wizard.scheduleTime}
              setScheduleTime={wizard.setScheduleTime}
              scheduleEmail={wizard.scheduleEmail}
              setScheduleEmail={wizard.setScheduleEmail}
              customInterval={wizard.customInterval}
              setCustomInterval={wizard.setCustomInterval}
              customUnit={wizard.customUnit}
              setCustomUnit={wizard.setCustomUnit}
              validationErrors={wizard.validationErrors}
              setValidationErrors={wizard.setValidationErrors}
              timeIntervals={wizard.timeIntervals}
            />
          )}

          {/* Wizard navigation */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '40px',
              paddingTop: '24px',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Button
              variant="outlined"
              onClick={wizard.handlePrev}
              disabled={wizard.isFirstStep}
              sx={btnOutlined}
            >
              Previous
            </Button>
            <Tooltip
              title={
                wizard.activeStep === 'layout' && !wizard.isLayoutComplete
                  ? 'Set a data source and query/intent for every slide before continuing'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={wizard.handleNext}
                  disabled={
                    (wizard.activeStep === 'creation-method' && (!wizard.pptxFile || wizard.extractLoading)) ||
                    (wizard.activeStep === 'layout' && (!wizard.isLayoutComplete || wizard.layoutSaveLoading)) ||
                    (wizard.activeStep === 'schedule' && wizard.scheduleSaveLoading)
                  }
                  startIcon={
                    (wizard.extractLoading || wizard.layoutSaveLoading || wizard.scheduleSaveLoading)
                      ? <CircularProgress size={18} color="inherit" />
                      : null
                  }
                  sx={btnPrimary}
                >
                  {wizard.extractLoading ? 'Extracting…'
                    : wizard.layoutSaveLoading ? 'Saving…'
                    : wizard.scheduleSaveLoading ? 'Saving…'
                    : wizard.isLastStep ? 'Finish'
                    : 'Next'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>}
      </Box>
    </Box>
  );
}

export default PresentationWizard;
